import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { ChevronLeft, Loader2, Plus, Building2, MapPin, Truck, Calendar, FileText, Search, Route, Check, Hash, Layers, X } from 'lucide-react';
import { GooglePlacesAutocomplete } from '../../components/ui/google-places-autocomplete';
import HaulageStopsEditor from '../../components/HaulageStopsEditor';
import { DatePicker } from '../../components/ui/date-picker';
import { format } from 'date-fns';
import MobileLayout from '../../components/mobile/MobileLayout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../../components/ui/sheet';
import { WheelTimePicker } from '../../components/mobile/WheelTimePicker';

const MobileScheduleCreateJob = () => {
  const navigate = useNavigate();
  const { api, user, tenantSettings, isFlagEnabled } = useMobileAuth();
  const isHaulage = user?.tenant_industry === 'haulage';
  const startingLocationRule = tenantSettings?.field_rules?.starting_location;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [depots, setDepots] = useState([]);
  const depotsEnabled = tenantSettings?.depots_enabled ?? false;

  const [form, setForm] = useState({
    customer_id: '', site_id: '', equipment_type: '', po_number: '',
    start_date: format(new Date(), 'yyyy-MM-dd'), end_date: '', start_time: '07:00',
    description: '', notes: '', status: 'not_allocated',
    job_type: 'standard', stops: [], starting_location: '', depot_id: '',
    project_id: '',
  });

  const [showCustomerSheet, setShowCustomerSheet] = useState(false);
  const [customerForm, setCustomerForm] = useState({ company_name: '', contact_name: '', phone: '', email: '' });
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);

  const [showSiteSheet, setShowSiteSheet] = useState(false);
  const [siteForm, setSiteForm] = useState({ name: '', street_address: '', suburb: '', state: '', postcode: '' });
  const [savingSite, setSavingSite] = useState(false);

  // ---- Projects ----
  const projectsEnabled = isFlagEnabled('projects');
  const [activeProjects, setActiveProjects] = useState([]);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');

  const filteredProjects = useMemo(() => {
    if (!projectSearch.trim()) return activeProjects;
    const q = projectSearch.toLowerCase();
    return activeProjects.filter(p => (p.name || '').toLowerCase().includes(q) || (p.customer_name || '').toLowerCase().includes(q));
  }, [activeProjects, projectSearch]);

  useEffect(() => { 
    fetchData();
    if (isHaulage) setForm(prev => ({ ...prev, job_type: 'haulage' }));
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [c, s, t] = await Promise.all([api.get('/api/customers', { params: { status: 'active' } }), api.get('/api/sites'), api.get('/api/equipment-types')]);
      setCustomers(c.data || []);
      setSites(s.data || []);
      setEquipmentTypes(t.data || []);
      if (tenantSettings?.depots_enabled) {
        api.get('/api/depots?active_only=true').then(r => setDepots(r.data || [])).catch(() => {});
      }
      // Fetch active projects if flag is on
      if (isFlagEnabled('projects')) {
        api.get('/api/projects', { params: { status: 'active', page_size: 200 } })
          .then(r => setActiveProjects(r.data?.items || []))
          .catch(() => {});
      }
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const customerSites = sites.filter(s => s.customer_id === form.customer_id);

  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers;
    const q = customerSearch.toLowerCase();
    return customers.filter(c => (c.company_name || '').toLowerCase().includes(q));
  }, [customers, customerSearch]);

  const selectedCustomerName = useMemo(() => {
    if (!form.customer_id) return '';
    return customers.find(c => c.id === form.customer_id)?.company_name || '';
  }, [customers, form.customer_id]);

  const selectedCustomerRequiresPO = useMemo(() => {
    if (!form.customer_id) return false;
    return customers.find(c => c.id === form.customer_id)?.requires_po || false;
  }, [customers, form.customer_id]);

  const handleCustomerChange = (v) => {
    if (v === '__add_new__') { setCustomerForm({ company_name: '', contact_name: '', phone: '', email: '' }); setShowCustomerSheet(true); }
    else {
      // If the new customer is different from the project's customer, clear project
      setForm(prev => {
        const clearProject = prev.project_id && prev._project_customer_id && v !== prev._project_customer_id;
        return { ...prev, customer_id: v, site_id: '', po_number: '', ...(clearProject ? { project_id: '', _project_customer_id: '' } : {}) };
      });
      setCustomerDropdownOpen(false);
      setCustomerSearch('');
    }
  };

  const handleSiteChange = (v) => {
    if (v === '__add_new__') { setSiteForm({ name: '', street_address: '', suburb: '', state: '', postcode: '' }); setShowSiteSheet(true); }
    else setForm({ ...form, site_id: v });
  };

  const handleProjectSelect = (projectId) => {
    if (!projectId) {
      setForm(prev => ({ ...prev, project_id: '', _project_customer_id: '' }));
      setProjectDropdownOpen(false);
      return;
    }
    const project = activeProjects.find(p => p.id === projectId);
    if (!project) return;
    setForm(prev => ({
      ...prev,
      project_id: project.id,
      _project_customer_id: project.customer_id,
      customer_id: project.customer_id,
      site_id: project.site_id || '',
    }));
    setProjectDropdownOpen(false);
    setProjectSearch('');
  };

  const handleCreateCustomer = async () => {
    if (!customerForm.company_name.trim()) { toast.error('Company name is required'); return; }
    setSavingCustomer(true);
    try {
      const res = await api.post('/api/customers', { customer_type: 'organisation', company_name: customerForm.company_name.trim(), phone: customerForm.phone.trim() || null, email: customerForm.email.trim() || null });
      toast.success('Customer created');
      setShowCustomerSheet(false);
      const refreshed = await api.get('/api/customers', { params: { status: 'active' } });
      setCustomers(refreshed.data);
      setForm({ ...form, customer_id: res.data.id, site_id: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create customer'); }
    finally { setSavingCustomer(false); }
  };

  const handleCreateSite = async () => {
    if (!siteForm.street_address.trim()) { toast.error('Address is required'); return; }
    setSavingSite(true);
    try {
      const res = await api.post('/api/sites', { ...siteForm, customer_id: form.customer_id });
      toast.success('Site created');
      setShowSiteSheet(false);
      const refreshed = await api.get('/api/sites');
      setSites(refreshed.data);
      setForm({ ...form, site_id: res.data.id });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create site'); }
    finally { setSavingSite(false); }
  };

  const handleSubmit = async () => {
    if (!form.customer_id) { toast.error('Please select a customer'); return; }
    if (selectedCustomerRequiresPO && !form.po_number?.trim()) { toast.error('PO Number is required for this customer'); return; }
    if (!form.start_date) { toast.error('Please select a start date'); return; }
    if (!form.description.trim()) { toast.error('Please enter a description'); return; }
    if (isHaulage && (!form.stops || form.stops.length < 2)) { toast.error('Add at least 2 stops (pickup + dropoff)'); return; }
    if (startingLocationRule?.enabled && startingLocationRule?.required && !form.starting_location?.trim()) { toast.error('Starting Location is required'); return; }
    setSaving(true);
    try {
      const submitData = { ...form, end_date: form.end_date || null, start_time: form.start_time || null, site_id: form.site_id || null, po_number: form.po_number?.trim() || null, starting_location: form.starting_location?.trim() || null, depot_id: form.depot_id || null };
      if (isHaulage) {
        submitData.site_id = null;
        const locs = (submitData.stops || []).sort((a, b) => a.order - b.order).map(s => s.suburb || s.address || `Stop ${s.order}`).filter((v, i, a) => !i || a[i-1] !== v);
        submitData.route_summary = locs.join(' → ');
      }
      const res = await api.post('/api/jobs', submitData);
      toast.success('Job created');
      navigate(`/mobile/schedule/${res.data.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === 'string') toast.error(detail);
      else if (Array.isArray(detail)) toast.error(detail.map(d => d.msg || JSON.stringify(d)).join('; '));
      else toast.error('Failed to create job');
    }
    finally { setSaving(false); }
  };

  if (loading) return <MobileLayout><div className="flex justify-center items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" /></div></MobileLayout>;

  return (
    <MobileLayout>
      <div className="bg-[var(--m-bg-card)] px-4 py-4 sticky top-0 z-10 safe-top">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/mobile/schedule')} className="text-[var(--m-text-secondary)] p-2" data-testid="schedule-create-back-btn"><ChevronLeft className="h-5 w-5" /></Button>
          <h1 className="text-[var(--m-text)] font-bold text-lg">Create Job</h1>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Project Selector - only when flag is on */}
        {projectsEnabled && (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
            <CardHeader className="pb-2"><CardTitle className="text-[var(--m-text)] text-base flex items-center gap-2"><Layers className="h-5 w-5 text-[#ffd108]" />Project</CardTitle></CardHeader>
            <CardContent>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setProjectDropdownOpen(!projectDropdownOpen)}
                  className="w-full flex items-center justify-between bg-[var(--m-bg)] border border-[var(--m-border)] text-[var(--m-text)] rounded-md px-3 py-2 text-sm"
                  data-testid="mobile-project-select"
                >
                  <span className={form.project_id ? 'text-[var(--m-text)]' : 'text-[var(--m-text-muted)]'}>
                    {form.project_id ? (activeProjects.find(p => p.id === form.project_id)?.name || 'Selected project') : 'Link to a project (optional)'}
                  </span>
                  <ChevronLeft className={`h-4 w-4 text-[var(--m-text-secondary)] transition-transform ${projectDropdownOpen ? 'rotate-90' : '-rotate-90'}`} />
                </button>
                {projectDropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-[var(--m-bg-card)] border border-[var(--m-border)] rounded-md shadow-lg overflow-hidden">
                    <div className="p-2 border-b border-[var(--m-border)]">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--m-text-muted)]" />
                        <Input
                          value={projectSearch}
                          onChange={(e) => setProjectSearch(e.target.value)}
                          placeholder="Search projects..."
                          className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)] pl-8 h-9 text-sm"
                          autoFocus
                          data-testid="mobile-project-search"
                        />
                      </div>
                    </div>
                    <div className="max-h-52 overflow-y-auto">
                      {form.project_id && (
                        <button
                          type="button"
                          onClick={() => handleProjectSelect(null)}
                          className="w-full text-left px-3 py-2.5 text-sm text-[var(--m-text-muted)] hover:bg-white/5 flex items-center gap-2"
                          data-testid="mobile-project-clear"
                        >
                          <X className="h-4 w-4" /> Clear project
                        </button>
                      )}
                      {filteredProjects.length === 0 ? (
                        <div className="px-3 py-4 text-center text-sm text-[var(--m-text-muted)]">No active projects</div>
                      ) : filteredProjects.map(p => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => handleProjectSelect(p.id)}
                          className={`w-full text-left px-3 py-2.5 text-sm hover:bg-white/5 flex items-center justify-between ${form.project_id === p.id ? 'text-[#ffd108]' : 'text-[var(--m-text)]'}`}
                          data-testid={`mobile-project-option-${p.id}`}
                        >
                          <div className="flex flex-col">
                            <span>{p.name}</span>
                            <span className="text-xs text-[var(--m-text-muted)]">{p.customer_name}</span>
                          </div>
                          {form.project_id === p.id && <Check className="h-4 w-4 flex-shrink-0" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {form.project_id && (
                <p className="text-xs text-[#ffd108] mt-2 flex items-center gap-1">
                  <Layers className="h-3 w-3" />
                  Customer and site pre-filled from project
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Customer */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2"><CardTitle className="text-[var(--m-text)] text-base flex items-center gap-2"><Building2 className="h-5 w-5 text-[#ffd108]" />Customer <span className="text-red-500">*</span></CardTitle></CardHeader>
          <CardContent>
            <div className="relative">
              <button
                type="button"
                onClick={() => setCustomerDropdownOpen(!customerDropdownOpen)}
                className="w-full flex items-center justify-between bg-[var(--m-bg)] border border-[var(--m-border)] text-[var(--m-text)] rounded-md px-3 py-2 text-sm"
                data-testid="schedule-create-customer"
              >
                <span className={selectedCustomerName ? 'text-[var(--m-text)]' : 'text-[var(--m-text-muted)]'}>
                  {selectedCustomerName || 'Select customer'}
                </span>
                <ChevronLeft className={`h-4 w-4 text-[var(--m-text-secondary)] transition-transform ${customerDropdownOpen ? 'rotate-90' : '-rotate-90'}`} />
              </button>
              {customerDropdownOpen && (
                <div className="absolute z-20 mt-1 w-full bg-[var(--m-bg-card)] border border-[var(--m-border)] rounded-md shadow-lg overflow-hidden">
                  <div className="p-2 border-b border-[var(--m-border)]">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--m-text-muted)]" />
                      <Input
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        placeholder="Search customers..."
                        className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)] pl-8 h-9 text-sm"
                        autoFocus
                        data-testid="schedule-create-customer-search"
                      />
                    </div>
                  </div>
                  <div className="max-h-52 overflow-y-auto">
                    <button
                      type="button"
                      onClick={() => { handleCustomerChange('__add_new__'); setCustomerDropdownOpen(false); }}
                      className="w-full text-left px-3 py-2.5 text-sm text-[#ffd108] hover:bg-[#ffd108]/10 flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />Add New Customer
                    </button>
                    {filteredCustomers.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => handleCustomerChange(c.id)}
                        className={`w-full text-left px-3 py-2.5 text-sm hover:bg-white/5 flex items-center justify-between ${form.customer_id === c.id ? 'text-[#ffd108]' : 'text-[var(--m-text)]'}`}
                      >
                        <span className="truncate">{c.company_name}</span>
                        {form.customer_id === c.id && <Check className="h-4 w-4 flex-shrink-0" />}
                      </button>
                    ))}
                    {filteredCustomers.length === 0 && customerSearch && (
                      <div className="px-3 py-4 text-sm text-[var(--m-text-muted)] text-center">No customers found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* PO Number */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-[var(--m-text)] text-base flex items-center gap-2">
              <Hash className="h-5 w-5 text-[#ffd108]" />
              PO Number {selectedCustomerRequiresPO ? <span className="text-red-500">*</span> : ''}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Input
              value={form.po_number}
              onChange={(e) => setForm({ ...form, po_number: e.target.value })}
              placeholder={selectedCustomerRequiresPO ? 'Required for this customer' : 'Enter PO number'}
              className={`bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)] ${selectedCustomerRequiresPO && !form.po_number?.trim() ? 'border-amber-500' : ''}`}
              data-testid="schedule-create-po-number"
            />
            {selectedCustomerRequiresPO && (
              <p className="text-xs text-amber-400 mt-1">This customer requires a PO number</p>
            )}
          </CardContent>
        </Card>

        {/* Site or Route Stops */}
        {isHaulage ? (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
            <CardHeader className="pb-2"><CardTitle className="text-[var(--m-text)] text-base flex items-center gap-2"><Route className="h-5 w-5 text-[#ffd108]" />Route Stops <span className="text-red-500">*</span></CardTitle></CardHeader>
            <CardContent>
              <HaulageStopsEditor
                stops={form.stops || []}
                onChange={(stops) => setForm({ ...form, stops })}
                country="AU"
                dark={true}
                sites={customerSites}
                onAddNewSite={() => setShowSiteSheet(true)}
              />
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
            <CardHeader className="pb-2"><CardTitle className="text-[var(--m-text)] text-base flex items-center gap-2"><MapPin className="h-5 w-5 text-[#ffd108]" />Site</CardTitle></CardHeader>
            <CardContent>
              <Select value={form.site_id} onValueChange={handleSiteChange} disabled={!form.customer_id}>
                <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" data-testid="schedule-create-site"><SelectValue placeholder={form.customer_id ? "Select site" : "Select customer first"} /></SelectTrigger>
                <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] max-h-64">
                  {form.customer_id && <SelectItem value="__add_new__" className="text-[#ffd108]"><span className="flex items-center gap-2"><Plus className="h-4 w-4" />Add New Site</span></SelectItem>}
                  {customerSites.map(s => <SelectItem key={s.id} value={s.id}>{s.name || s.street_address}</SelectItem>)}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        )}

        {/* Starting Location — gated on field_rules.starting_location.enabled */}
        {startingLocationRule?.enabled && (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
            <CardHeader className="pb-2"><CardTitle className="text-[var(--m-text)] text-base flex items-center gap-2"><Route className="h-5 w-5 text-[#ffd108]" />Starting Location {startingLocationRule?.required ? <span className="text-red-500">*</span> : ''}</CardTitle></CardHeader>
            <CardContent>
              <GooglePlacesAutocomplete
                placeholder="Where will workers start from?"
                country="AU"
                value={form.starting_location}
                onChange={(e) => setForm({ ...form, starting_location: e.target.value })}
                onPlaceSelect={(place) => setForm({ ...form, starting_location: place.formatted_address || place.street_address || '' })}
                className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]"
                data-testid="schedule-create-starting-location"
              />
            </CardContent>
          </Card>
        )}

        {/* Equipment */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2"><CardTitle className="text-[var(--m-text)] text-base flex items-center gap-2"><Truck className="h-5 w-5 text-[#ffd108]" />Equipment Type</CardTitle></CardHeader>
          <CardContent>
            <Select value={form.equipment_type} onValueChange={(v) => setForm({ ...form, equipment_type: v })}>
              <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" data-testid="schedule-create-equipment"><SelectValue placeholder="Select equipment type" /></SelectTrigger>
              <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] max-h-64">{equipmentTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Depot */}
        {depotsEnabled && (
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2"><CardTitle className="text-[var(--m-text)] text-base flex items-center gap-2"><MapPin className="h-5 w-5 text-[#ffd108]" />Depot</CardTitle></CardHeader>
          <CardContent>
            <Select value={form.depot_id || '__none__'} onValueChange={(v) => setForm({ ...form, depot_id: v === '__none__' ? '' : v })}>
              <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" data-testid="schedule-create-depot"><SelectValue placeholder="Select depot" /></SelectTrigger>
              <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] max-h-64">
                <SelectItem value="__none__">No depot</SelectItem>
                {depots.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
        )}

        {/* Dates */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2"><CardTitle className="text-[var(--m-text)] text-base flex items-center gap-2"><Calendar className="h-5 w-5 text-[#ffd108]" />Schedule <span className="text-red-500">*</span></CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label className="text-[var(--m-text-secondary)] text-xs uppercase">Start Date</Label><DatePicker value={form.start_date} onChange={(d) => setForm({ ...form, start_date: d })} className="w-full bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" testId="schedule-create-start-date" /></div>
              <div className="space-y-2"><Label className="text-[var(--m-text-secondary)] text-xs uppercase">End Date</Label><DatePicker value={form.end_date} onChange={(d) => setForm({ ...form, end_date: d })} className="w-full bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" minDate={form.start_date} testId="schedule-create-end-date" /></div>
            </div>
            <div className="space-y-2">
              <Label className="text-[var(--m-text-secondary)] text-xs uppercase">Start Time</Label>
              <WheelTimePicker
                value={form.start_time || '07:00'}
                onChange={(v) => setForm({ ...form, start_time: v })}
                className="w-full bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]"
                testId="schedule-create-start-time"
              />
            </div>
          </CardContent>
        </Card>

        {/* Description */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2"><CardTitle className="text-[var(--m-text)] text-base flex items-center gap-2"><FileText className="h-5 w-5 text-[#ffd108]" />Description <span className="text-red-500">*</span></CardTitle></CardHeader>
          <CardContent><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" rows={3} placeholder="Job description..." data-testid="schedule-create-description" /></CardContent>
        </Card>

        {/* Notes */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2"><CardTitle className="text-[var(--m-text)] text-base flex items-center gap-2"><FileText className="h-5 w-5 text-[var(--m-text-muted)]" />Notes</CardTitle></CardHeader>
          <CardContent><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" rows={2} placeholder="Additional notes..." /></CardContent>
        </Card>

        <Button className="w-full btn-volt py-6" onClick={handleSubmit} disabled={saving} data-testid="schedule-create-submit">
          {saving ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Plus className="h-5 w-5 mr-2" />}Create Job
        </Button>

        {/* Spacer for bottom nav bar */}
        <div className="h-24" />
      </div>

      {/* Quick Add Customer */}
      <Sheet open={showCustomerSheet} onOpenChange={setShowCustomerSheet}>
        <SheetContent side="bottom" className="bg-[var(--m-bg-card)] border-[var(--m-border)] h-[60vh]">
          <SheetHeader><SheetTitle className="text-[var(--m-text)]">Add Customer</SheetTitle></SheetHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label className="text-[var(--m-text-secondary)] text-xs uppercase">Company Name <span className="text-red-500">*</span></Label><Input value={customerForm.company_name} onChange={(e) => setCustomerForm({ ...customerForm, company_name: e.target.value })} className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" /></div>
            <div className="space-y-2"><Label className="text-[var(--m-text-secondary)] text-xs uppercase">Phone</Label><Input value={customerForm.phone} onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })} className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" /></div>
            <div className="space-y-2"><Label className="text-[var(--m-text-secondary)] text-xs uppercase">Email</Label><Input value={customerForm.email} onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })} className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" /></div>
          </div>
          <SheetFooter className="flex gap-2">
            <Button variant="outline" className="flex-1 border-[var(--m-border)]" onClick={() => setShowCustomerSheet(false)}>Cancel</Button>
            <Button className="flex-1 btn-volt" onClick={handleCreateCustomer} disabled={savingCustomer}>{savingCustomer ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Quick Add Site */}
      <Sheet open={showSiteSheet} onOpenChange={setShowSiteSheet}>
        <SheetContent side="bottom" className="bg-[var(--m-bg-card)] border-[var(--m-border)] h-[75vh]">
          <SheetHeader><SheetTitle className="text-[var(--m-text)]">Add Site</SheetTitle></SheetHeader>
          <div className="space-y-4 py-4 overflow-y-auto">
            <div className="space-y-2">
              <Label className="text-[var(--m-text-secondary)] text-xs uppercase flex items-center gap-1"><Search className="h-3 w-3" />Search Address</Label>
              <GooglePlacesAutocomplete
                placeholder="Start typing an address..."
                country="AU"
                onPlaceSelect={(place) => {
                  setSiteForm(prev => ({
                    ...prev,
                    name: [place.street_address, place.suburb].filter(Boolean).join(', ') || place.name || prev.name,
                    street_address: place.street_address || '',
                    suburb: place.suburb || '',
                    state: place.state || '',
                    postcode: place.postcode || ''
                  }));
                }}
                className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]"
                data-testid="mobile-site-address-autocomplete"
              />
              <p className="text-xs text-[var(--m-text-muted)]">Or enter manually below</p>
            </div>
            <div className="space-y-2"><Label className="text-[var(--m-text-secondary)] text-xs uppercase">Site Name</Label><Input value={siteForm.name} onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })} className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" data-testid="mobile-site-name" /></div>
            <div className="space-y-2"><Label className="text-[var(--m-text-secondary)] text-xs uppercase">Street Address <span className="text-red-500">*</span></Label><Input value={siteForm.street_address} onChange={(e) => setSiteForm({ ...siteForm, street_address: e.target.value })} className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" data-testid="mobile-site-street" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label className="text-[var(--m-text-secondary)] text-xs uppercase">Suburb</Label><Input value={siteForm.suburb} onChange={(e) => setSiteForm({ ...siteForm, suburb: e.target.value })} className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" data-testid="mobile-site-suburb" /></div>
              <div className="space-y-2"><Label className="text-[var(--m-text-secondary)] text-xs uppercase">State</Label><Input value={siteForm.state} onChange={(e) => setSiteForm({ ...siteForm, state: e.target.value })} className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" data-testid="mobile-site-state" /></div>
            </div>
            <div className="space-y-2"><Label className="text-[var(--m-text-secondary)] text-xs uppercase">Postcode</Label><Input value={siteForm.postcode} onChange={(e) => setSiteForm({ ...siteForm, postcode: e.target.value })} className="bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)] w-1/2" data-testid="mobile-site-postcode" /></div>
          </div>
          <SheetFooter className="flex gap-2">
            <Button variant="outline" className="flex-1 border-[var(--m-border)]" onClick={() => setShowSiteSheet(false)}>Cancel</Button>
            <Button className="flex-1 btn-volt" onClick={handleCreateSite} disabled={savingSite}>{savingSite ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </MobileLayout>
  );
};

export default MobileScheduleCreateJob;
